<template>
  <div class="car-form-container car-vehicle-identification-license-plate" data-js="license-plate-identification">
    <!-- First enter license plate -->
    <div v-if="!licensePlateRequest" class="car-vehicle-identification-form">
      <div class="car-form-item car-form-item-license-plate" :class="{'car-form-item-error': !$v.form.licensePlate.required && $v.form.licensePlate.$error}">
        <label for="licensePlate">{{$t('licensePlateIdentification.licensePlatePlaceholder')}}*</label>
        <input type="text"
               id="licensePlate"
               :class="{ 'car-filled': form.licensePlate }"
               :placeholder="$t('licensePlateIdentification.licensePlatePlaceholder')"
               v-model="form.licensePlate">
        <div class="car-error-message" v-if="!$v.form.licensePlate.required && $v.form.licensePlate.$error">
          {{$t('licensePlateIdentification.errors.licensePlate')}}
        </div>
      </div>
    </div>

    <!-- Second verify sms code -->
    <div v-if="licensePlateRequest && !vehicleDetails" class="car-vehicle-identification-form">
      <div class="car-form-item car-form-item-license-plate">
        <label for="verificationCode">{{$t('licensePlateIdentification.verificationCodePlaceHolder')}}</label>
        <input type="text"
               id="verificationCode"
               :class="{ 'car-filled': verificationCode }"
               :placeholder="$t('licensePlateIdentification.verificationCodePlaceHolder')"
               v-model="verificationCode">
      </div>
    </div>

    <!-- Third show identified vehicle, show mileage input, show registration date and go to valuation page-->
    <div v-if="licensePlateRequest && vehicleDetails" class="car-vehicle-identification-form">
      <div class="vehicle-details-item">
        <div class="vehicle-details-item-key">
          {{ $t("licensePlateIdentification.vehicleBrand") }}
        </div>
        <div class="vehicle-details-item-value">
          {{ vehicleDetails.manufacturerName }}
        </div>
      </div>

      <div class="vehicle-details-item">
        <div class="vehicle-details-item-key">
          {{ $t("licensePlateIdentification.vehicleModel") }}
        </div>
        <div class="vehicle-details-item-value">
          {{ vehicleDetails.baseModelName }}
        </div>
      </div>

      <div v-if="vehicleDetails.registrationDate" class="vehicle-details-item">
        <div class="vehicle-details-item-key">
          {{ $t("licensePlateIdentification.registrationDate") }}
        </div>
        <div class="vehicle-details-item-value">
          {{ vehicleDetails.registrationDate }}
        </div>
      </div>

      <div v-if="!vehicleDetails.registrationDate" class="car-form-item">
        <div class="car-catalog-identification__container--select">
          <v-select
            class="car-v-select"
            name="regYear"
            v-model="regYear"
            :options="availableYears"
            :reduce="(item) => item.value"
            :placeholder="$t('licensePlateIdentification.registrationYear')"
          />
        </div>
      </div>

      <div v-if="!vehicleDetails.registrationDate" class="car-form-item">
        <div class="car-catalog-identification__container--select">
          <v-select
            class="car-v-select"
            name="regMonth"
            v-model="regMonth"
            :options="availableMonths"
            :reduce="(item) => item.value"
            :placeholder="$t('licensePlateIdentification.registrationMonth')"
          />
        </div>
      </div>
    </div>

    <!-- in case of error -->
    <div v-if="identificationError" class="car-vehicle-identification-form">
      <div class="car-error-message">{{ identificationError }}</div>
    </div>

    <!-- First enter license plate -->
    <div v-if="!licensePlateRequest" class="car-form-buttons car-buttons">
      <primary-btn
        @click="licensePlateLookup"
        class="car-btn-primary"
        data-js="license-plate-identification--btn"
        :loading="loading">
        {{ $t('licensePlateIdentification.identifyBtn') }}
      </primary-btn>
    </div>

    <!-- Second verify sms code -->
    <div v-if="licensePlateRequest && !vehicleDetails" class="car-form-buttons car-buttons">
      <primary-btn
        @click="verifyLicensePlateLookup"
        class="car-btn-primary"
        data-js="license-plate-identification--btn"
        :loading="loading"
        :disabled="!verificationCode"
      >
        {{ $t('licensePlateIdentification.verifyBtn') }}
      </primary-btn>
    </div>

    <!-- Third show identified vehicle, show mileage input, show registration date and go to valuation page-->
    <div v-if="licensePlateRequest && vehicleDetails" class="car-form-buttons car-buttons">
      <primary-btn
        @click="useVehicle"
        class="car-btn-primary"
        data-js="license-plate-identification--btn"
        :loading="loading"
        :disabled="!isAllowedUseVehicle"
      >
        {{ $t('licensePlateIdentification.useVehicleBtn') }}
      </primary-btn>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';

import PrimaryBtn from '@/components/PrimaryBtn';
import engageApi from '@/services/engageApi';
import regionService from '@/services/regionService';
import vehicleService from '@/services/vehicleService';

export default {
  name: 'LicensePlateIdentification',
  data() {
    return {
      form: {
        licensePlate: ''
      },
      loading: false,
      mileage: 0,
      licensePlateRequest: null,
      verificationCode: null,
      identificationError: null,
      vehicleDetails: null,
      regYear: null,
      regMonth: null
    }
  },
  validations: {
    form: {
      licensePlate: {
        required
      }
    }
  },
  computed: {
    isAllowedUseVehicle() {
      return this.vehicleDetails?.registrationDate || (this.regYear && this.regMonth);
    },
    availableYears() {
      const arr = [];
      for (let i = moment().year(); i >= moment().year() - 15; i--) {
        arr.push({ label: i.toString(), value: i });
      }

      return arr;
    },
    availableMonths() {
      return moment.months().map((it, index) => {
        return { label: it, value: index + 1 };
      });
    }
  },
  methods: {
    async licensePlateLookup() {
      this.$v.$touch();
      if (this.$v.$invalid || this.loading) { return; }

      this.loading = true;

      try {
        this.licensePlateRequest = await engageApi.requestIdentifyLicensePlateFromEngage(this.form.licensePlate);
      } catch (err) {
        if (typeof err !== 'string' && err.isAxiosError && err.response.status === 404) {
          this.identificationError = this.$t('licensePlateIdentification.errors.notfound', { licensePlate: this.form.licensePlate });
        } else {
          this.identificationError = err;
        }
      }

      this.loading = false;
    },
    async verifyLicensePlateLookup() {
      if (!(this.licensePlateRequest && this.licensePlateRequest.token) || this.loading) return;

      this.loading = true;
      this.identificationError = null;

      try {
        const engageData = await engageApi.getIdentifyLicensePlateFromEngage(this.licensePlateRequest.token, this.verificationCode)

        if (engageData?.customer) {
          this.$store.commit('wizardData/SET_ENGAGE_CUSTOMER', engageData.customer);
        }
        if (engageData?.vehicle) {
          this.$store.commit('wizardData/SET_ENGAGE_VEHICLE', engageData.vehicle);
        }

        if (engageData?.vehicle?.ecode) {
          const vehicleTypeDetail = await vehicleService.getVehicleTypeDetails(engageData.vehicle.ecode);

          if (vehicleTypeDetail.length) {
            const vehicleDimensions = await vehicleService.getDimensionValues({ externalIds: [
              vehicleTypeDetail[0].dimensionValues.find((it) => it.includes('AVMO-')),
              vehicleTypeDetail[0].dimensionValues.find((it) => it.includes('FUEL_ID-'))
            ]
            }).then((res) => res.data);

            this.vehicleDetails = {
              manufacturerName: engageData.vehicle.brandName,
              baseModelName: engageData.vehicle.baseModelName,
              eCode: engageData.vehicle.ecode,
              container: engageData.vehicle.container,
              registrationDate: engageData.vehicle.registrationDate ? moment(new Date(engageData.vehicle.registrationDate)) : null,
              vehicle: vehicleTypeDetail[0],
              engineDimension: vehicleDimensions.find((it) => it.externalId.includes('AVMO-')),
              fuelDimension: vehicleDimensions.find((it) => it.externalId.includes('FUEL_ID-'))
            };
          }
        }

        if (!this.vehicleDetails) {
          this.identificationError = this.$t('licensePlateIdentification.errors.notAllRequiredAttributes');
        }
      } catch (err) {
        if (typeof err !== 'string' && err.isAxiosError && err.response.status === 403) {
          this.identificationError = this.$t('licensePlateIdentification.errors.wrongCode');
        } else {
          this.identificationError = err;
        }
      }

      this.loading = false;
    },
    async useVehicle() {
      const date = this.vehicleDetails.registrationDate || moment().year(this.regYear).month(this.regMonth).day(1);

      this.$store.commit('vehicleIdentification/SET_VEHICLETYPE', {
        ...this.vehicleDetails.vehicle,
        year: date.year(),
        month: date.month(),
        constructionTime: date
      });

      let selectedEquipments = [];
      selectedEquipments.push(this.vehicleDetails.engineDimension);
      selectedEquipments.push(this.vehicleDetails.fuelDimension);
      this.$store.commit('vehicleEquipments/SET_SELECTED_EQUIPMENTS', selectedEquipments);

      regionService.nextRoute(this);
    }
  },
  components: {
    PrimaryBtn
  }
}
</script>

<style lang="scss" scoped>
  .vehicle-details-item {
    &-key {
      font-weight: bold;
    }

    &-value {
      padding: 5px;
    }
  }
</style>
